/* Elasticsearch hit fields use leading underscores (_id, _type, etc.). */
/* eslint-disable no-underscore-dangle */

import { action, observable, computed } from 'mobx';
import merge from 'deepmerge';
import { extractFromSlug } from 'helpers/utils';
import { CatalogEntry } from 'types';
import {
  SearchCourseHeader,
  SearchHighlight,
  SearchHit,
  SearchQueryParams,
  SearchSuggestEntry,
} from 'types/search';
import { search } from 'services/SearchService';
import type { IRootStore } from './IRootStore';

export type SearchWithin = 'all' | 'catalog' | 'skills-paths';

export type SearchOption = {
  label: string;
  value: {
    query: string;
    searchWithin: SearchWithin;
  };
};

export type SearchSuggestionTerm = [text: string, isSuggested: boolean];

/**
 * A catalog offering (or fallback hit/header) with optional page hits grouped
 * by version tag. Search V1 renders these via CatalogCard
 */
export type GroupedSearchResult = (
  | CatalogEntry
  | SearchHit
  | SearchCourseHeader
) & {
  highlight?: SearchHighlight;
  pages?: Record<string, SearchHit[]>;
};

const copyCatalogEntry = (entry) => (entry ? { ...entry } : entry);

class SearchStore {
  rootStore: IRootStore;

  @observable query = '';

  @observable courses: string | null = null;

  @observable searchResults: SearchHit[] = [];

  @observable suggestionResults: SearchSuggestEntry[] = [];

  @observable _currentPage = 1;

  @observable entriesPerPage = 10;

  @observable finishedSearch = true;

  @observable searchError: Error | null = null;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action search = async (
    searchTerm: string,
    courses?: string | null,
  ): Promise<SearchHit[]> => {
    try {
      this.finishedSearch = false;
      this.searchError = null;
      const query: SearchQueryParams = { q: searchTerm, size: 200 };
      if (courses) {
        query.course = courses;
      }
      const response = await search(query);
      const searchResults = response?.hits?.hits;

      if (searchResults) {
        this.searchResults = searchResults;
        this.suggestionResults = response.suggest?.suggestion ?? [];
      }
    } catch (e) {
      this.searchResults = [];
      this.suggestionResults = [];
      this.searchError = e instanceof Error ? e : new Error(String(e));
      console.error(e);
    }
    this.finishedSearch = true;
    return this.searchResults;
  };

  @computed get totalPages(): number {
    return Math.ceil(this.results.length / this.entriesPerPage) || 1;
  }

  @computed get currentPage(): number {
    if (this._currentPage > this.totalPages) {
      return 1;
    }

    return this._currentPage;
  }

  @action setCurrentPage = (page = 1) => {
    this._currentPage = page;
  };

  @computed get paginatedEntries(): GroupedSearchResult[] {
    const startIndex = (this.currentPage - 1) * this.entriesPerPage;
    return this.results.slice(startIndex, startIndex + this.entriesPerPage);
  }

  @computed get options(): SearchOption[] {
    const { userStore } = this.rootStore;

    const optionArray: SearchOption[] = [
      {
        label: this.query,
        value: { query: this.query, searchWithin: 'all' },
      },
    ];

    if (!userStore?.isAlaCarte && !userStore?.isExternalSubscriber) {
      optionArray.push(
        {
          label: this.query,
          value: { query: this.query, searchWithin: 'catalog' },
        },
        {
          label: this.query,
          value: { query: this.query, searchWithin: 'skills-paths' },
        },
      );
    }

    return optionArray;
  }

  @computed get groupedSearchResults(): Record<string, GroupedSearchResult> {
    // API returns results in a flat array, so we need to group them by course code here
    const dict: Record<string, GroupedSearchResult> = {};
    const { catalogStore } = this.rootStore;

    if (!this.searchResults.length) {
      return dict;
    }

    this.searchResults
      .slice()
      // Sort by scores, with higher scores first in the list
      .sort((a, b) => (a._score < b._score ? 1 : -1))
      .forEach((hit) => {
        let slug: string | undefined;
        let code: string | undefined;

        switch (hit._type) {
          case 'expert_seminar':
            slug = hit._id;
            ({ code } = extractFromSlug(slug));
            dict[code] =
              copyCatalogEntry(catalogStore.allUniqueCatalogEntries[code]) ||
              hit;
            dict[code] = merge(dict[code], { highlight: hit.highlight });
            break;
          case 'course':
            slug = hit._source?.course_header?.version_tag;
            if (!slug) {
              [slug] = hit._id.split('@@');
            }
            try {
              ({ code } = extractFromSlug(slug));
            } catch (err) {
              return;
            }
            dict[code] =
              copyCatalogEntry(catalogStore.allUniqueCatalogEntries[code]) ||
              hit._source?.course_header;

            break;
          case 'page':
            slug = hit._source?.course_header?.version_tag;
            ({ code } = extractFromSlug(slug));

            if (!dict[code]) {
              dict[code] =
                copyCatalogEntry(catalogStore.allUniqueCatalogEntries[code]) ||
                hit._source?.course_header;

              if (!dict[code]) {
                console.error(hit);
              }
            }
            if (!dict[code].pages) {
              dict[code].pages = {};
            }
            if (!dict[code].pages[slug]) {
              dict[code].pages[slug] = [];
            }
            dict[code].pages[slug].push(hit);
            break;
          default:
            break;
        }
      });

    return dict;
  }

  @computed get results(): GroupedSearchResult[] {
    return Object.values(this.groupedSearchResults);
  }

  @computed get suggestion(): SearchSuggestionTerm[] {
    return this.suggestionResults.map((entry) =>
      entry.options.length
        ? [entry.options[0].text, true]
        : [entry.text, false],
    );
  }

  clearSearch = () => {
    this.query = '';
    this.searchResults = [];
  };
}

export default SearchStore;
